/**
 * Newsletter popup - shows on page load, uses localStorage for:
 * - subscribed: never show again
 * - dismissed: show again after X days (configurable)
 */
class NewsletterPopup extends HTMLElement {
  constructor() {
    super();
    this.closeButtons = this.querySelectorAll('.newsletter-popup__close, .newsletter-popup__overlay');
    this.form = this.querySelector('form');
    this.storageKey = this.dataset.storageKey || 'newsletter_popup';
    this.delay = parseInt(this.dataset.delay || '2', 10) * 1000;
    this.dismissDays = parseInt(this.dataset.dismissDays || '7', 10);
    this.showTimeout = null;
    this.activeElement = null;

    this.close = this.close.bind(this);
    this.open = this.open.bind(this);
  }

  connectedCallback() {
    this.closeButtons.forEach((btn) => btn.addEventListener('click', this.close));
    this.addEventListener('keyup', (e) => e.code === 'Escape' && this.close());

    if (this.form) {
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    this.scheduleShow();
  }

  disconnectedCallback() {
    if (this.showTimeout) clearTimeout(this.showTimeout);
  }

  shouldShow() {
    try {
      if (localStorage.getItem(`${this.storageKey}_subscribed`) === '1') {
        return false;
      }
      const dismissed = localStorage.getItem(`${this.storageKey}_dismissed`);
      if (dismissed && this.dismissDays > 0) {
        const dismissedAt = parseInt(dismissed, 10);
        const now = Date.now();
        const daysSince = (now - dismissedAt) / (1000 * 60 * 60 * 24);
        if (daysSince < this.dismissDays) return false;
      }
      return true;
    } catch (e) {
      return true;
    }
  }

  scheduleShow() {
    if (!this.shouldShow()) return;
    this.showTimeout = setTimeout(this.open, this.delay);
  }

  open() {
    if (this.showTimeout) {
      clearTimeout(this.showTimeout);
      this.showTimeout = null;
    }
    this.activeElement = document.activeElement;
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-visible');
    document.body.classList.add('overflow-hidden');

    const focusTarget = this.querySelector('input[type="email"]') || this.querySelector('.newsletter-popup__close');
    if (typeof trapFocus === 'function' && focusTarget) {
      trapFocus(this, focusTarget);
    }
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-visible');
    document.body.classList.remove('overflow-hidden');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    this.activeElement = null;

    this.saveDismissed();
  }

  saveDismissed() {
    try {
      if (this.dismissDays > 0) {
        localStorage.setItem(`${this.storageKey}_dismissed`, String(Date.now()));
      }
    } catch (e) {
      // localStorage may be full or disabled
    }
  }

  saveSubscribed() {
    try {
      localStorage.setItem(`${this.storageKey}_subscribed`, '1');
    } catch (e) {
      // localStorage may be full or disabled
    }
  }

  onFormSubmit(e) {
    e.preventDefault();
    const submitBtn = this.form.querySelector('button[type="submit"]');
    const fieldWrapper = this.form.querySelector('.newsletter-form__field-wrapper');
    const errorEl = this.form.querySelector('.newsletter-form__message:not(.newsletter-form__message--success)');
    const successEl = this.form.querySelector('.newsletter-form__message--success');

    if (submitBtn) submitBtn.setAttribute('aria-busy', 'true');

    fetch(this.form.action, {
      method: 'POST',
      body: new FormData(this.form),
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const successMsg = doc.querySelector('.newsletter-form__message--success, .form__message--success, .note.form-success');
        const errorMsg = doc.querySelector('.newsletter-form__message:not(.newsletter-form__message--success), .form__message:not(.form__message--success)');
        const successText = (doc.body && doc.body.textContent || '').toLowerCase();
        const hasSuccess = successMsg || successText.includes('thanks for subscribing') || successText.includes('grazie per esserti iscritto');

        if (successMsg || hasSuccess) {
          this.saveSubscribed();
          if (fieldWrapper) fieldWrapper.style.display = 'none';
          if (successEl) {
            successEl.style.display = '';
            successEl.removeAttribute('hidden');
          } else if (fieldWrapper && fieldWrapper.parentNode) {
            const div = document.createElement('div');
            div.className = 'newsletter-form__message newsletter-form__message--success form__message';
            div.innerHTML = successMsg ? successMsg.innerHTML : '<span>Thanks for subscribing</span>';
            fieldWrapper.parentNode.insertBefore(div, fieldWrapper.nextSibling);
          }
          setTimeout(() => this.close(), 2000);
        } else if (errorMsg && errorEl) {
          errorEl.innerHTML = errorMsg.innerHTML;
          errorEl.hidden = false;
        }
      })
      .catch(() => {
        if (errorEl) {
          errorEl.textContent = 'Something went wrong. Please try again.';
          errorEl.hidden = false;
        }
      })
      .finally(() => {
        if (submitBtn) submitBtn.removeAttribute('aria-busy');
      });
  }

  markSubscribed() {
    this.saveSubscribed();
    this.close();
  }
}

customElements.define('newsletter-popup', NewsletterPopup);
